import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import './App.css';
import NFTDetails from './NFTDetails';

function App() {
  // State for managing NFTs, wallet connection, and UI state
  const [nfts, setNfts] = useState([]);
  const [contractAddresses] = useState([
    '0xbc4ca0eda7647a8ab7c2061c2e118a18a936f13d', // Bored Ape Yacht Club
    '0x60e4d786628fea6478f785a6d7e704777c86a7c6', // Mutant Ape Yacht Club
    '0x34d85c9cdeb23fa97cb08333b511ac86e1c4e258', // Otherdeed for Otherside
  ]);
  const [currentContract, setCurrentContract] = useState('');
  const [walletConnected, setWalletConnected] = useState(false);
  const [account, setAccount] = useState('');
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState('');
  const [selectedNft, setSelectedNft] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [listingPrice, setListingPrice] = useState('');
  const [selectedChain, setSelectedChain] = useState('ethereum');
  const [activeTab, setActiveTab] = useState('featured');
  const [featuredNfts, setFeaturedNfts] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsNft, setDetailsNft] = useState(null);
  
  // Available chains
  const chains = [
    { id: 'ethereum', name: 'Ethereum' },
    { id: 'matic', name: 'Polygon' },
    { id: 'arbitrum', name: 'Arbitrum' },
    { id: 'optimism', name: 'Optimism' },
    { id: 'avalanche', name: 'Avalanche' },
    { id: 'base', name: 'Base' }
  ];

  // Hardcoded featured NFTs
  const featuredNftsList = [
    { chain: 'ethereum', contract: '0x16edf9d65a54e1617921a8125d77ef48c4e8c449', identifier: '335' },
    { chain: 'ethereum', contract: '0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270', identifier: '163000813' },
    { chain: 'ethereum', contract: '0xbc4ca0eda7647a8ab7c2061c2e118a18a936f13d', identifier: '8520' },
    { chain: 'ethereum', contract: '0x60e4d786628fea6478f785a6d7e704777c86a7c6', identifier: '21838' }
  ];

  // Set up axios with API key if available
  useEffect(() => {
    const apiKey = process.env.REACT_APP_OPENSEA_API_KEY;
    if (apiKey) {
      axios.defaults.headers.common['X-API-KEY'] = apiKey;
    }
    axios.defaults.headers.common['Accept'] = 'application/json';
  }, []);

  // Fetch featured NFTs when component mounts
  useEffect(() => {
    fetchFeaturedNfts();
  }, []);

  // Fetch featured NFTs
  const fetchFeaturedNfts = async () => {
    setLoading(true);
    setError('');

    const fetchedNfts = [];

    try {
      // Use the same metadata fetching method as Explore
      for (const nft of featuredNftsList) {
        const apiUrl = `https://api.opensea.io/api/v2/chain/${nft.chain}/contract/${nft.contract}/nfts/${nft.identifier}`;
        console.log(`Fetching NFT metadata from: ${apiUrl}`);

        const response = await axios.get(apiUrl);

        if (response.data && response.data.nft) {
          // The actual NFT data is in response.data.nft
          const nftData = response.data.nft;
          
          // Create a structured object that matches your rendering expectations
          const nftItem = {
            identifier: nftData.identifier,
            id: nftData.identifier,
            name: nftData.name,
            collection: nftData.collection,
            contract: nftData.contract,
            chain: nft.chain,
            image_url: nftData.image_url,
            display_image_url: nftData.display_image_url,
            price: null,  // Price will be updated later
            priceSymbol: 'ETH',
          };

          fetchedNfts.push(nftItem);
        }
      }

      // Update state with metadata first
      setFeaturedNfts(fetchedNfts);

      // Fetch listing prices separately, using EXACTLY the same URL pattern as your code
      for (let i = 0; i < fetchedNfts.length; i++) {
        const nft = fetchedNfts[i];
        const listingsUrl = `https://api.opensea.io/api/v2/orders/${nft.chain}/seaport/listings?asset_contract_address=${nft.contract}&token_ids=${nft.id}`;
        console.log(`Fetching listing price from: ${listingsUrl}`);

        const listingsResponse = await axios.get(listingsUrl);

        if (listingsResponse.data && listingsResponse.data.orders?.length > 0) {
          const sortedListings = listingsResponse.data.orders.sort((a, b) => {
            return parseFloat(a.current_price) - parseFloat(b.current_price);
          });

          const bestListing = sortedListings[0];
          const price = (parseFloat(bestListing.current_price) / 1e18).toFixed(2);

          // Update price for the specific NFT
          setFeaturedNfts((prevNfts) => {
            const updatedNfts = [...prevNfts];
            updatedNfts[i] = {
              ...updatedNfts[i],
              price,
            };
            return updatedNfts;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching featured NFTs:', error);
      setError('Failed to fetch featured NFTs. Please try again.');
    }

    setLoading(false);
  };

  // Connect to MetaMask wallet
  const connectWallet = async () => {
    try {
      if (window.ethereum) {
        setLoading(true);
        // Request account access
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        const account = accounts[0];
        
        // Create ethers provider and signer
        const ethersProvider = new ethers.providers.Web3Provider(window.ethereum);
        const ethersSigner = ethersProvider.getSigner();
        
        // Set provider for future features that may need direct provider access
        setProvider(ethersProvider);
        setSigner(ethersSigner);
        setAccount(account);
        setWalletConnected(true);
        setLoading(false);
        
        // Listen for account changes
        window.ethereum.on('accountsChanged', (accounts) => {
          if (accounts.length === 0) {
            // User disconnected wallet
            setWalletConnected(false);
            setAccount('');
          } else {
            setAccount(accounts[0]);
          }
        });
      } else {
        setError('Please install MetaMask to use this application');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error connecting wallet:', error);
      setError('Failed to connect wallet');
      setLoading(false);
    }
  };

  // Fetch NFTs from OpenSea API V2
  const fetchNFTs = async (contractAddress, resetData = true) => {
    if (!contractAddress) return;
    
    try {
      setLoading(true);
      setError('');
      
      if (resetData) {
        setNfts([]);
        setNextCursor('');
      }
      
      const limit = 20;
      const apiUrl = `https://api.opensea.io/api/v2/chain/${selectedChain}/contract/${contractAddress}/nfts`;
      
      const params = {
        limit,
        next: resetData ? undefined : nextCursor
      };
      
      console.log(`Fetching NFTs from: ${apiUrl}`);
      const response = await axios.get(apiUrl, { params });
      
      if (response.data && response.data.nfts) {
        // For each NFT, fetch its listing price
        const nftsWithPrices = await Promise.all(
          response.data.nfts.map(async (nft) => {
            try {
              const listingsUrl = `https://api.opensea.io/api/v2/chain/${selectedChain}/contract/${contractAddress}/nfts/${nft.identifier}/listings`;
              const listingsResponse = await axios.get(listingsUrl);
              
              let price = null;
              let priceSymbol = 'ETH';
              
              if (listingsResponse.data && listingsResponse.data.listings && listingsResponse.data.listings.length > 0) {
                // Find the lowest price listing
                const sortedListings = listingsResponse.data.listings.sort((a, b) => {
                  return parseFloat(a.price.current.value) - parseFloat(b.price.current.value);
                });
                
                const bestListing = sortedListings[0];
                price = parseFloat(bestListing.price.current.value).toFixed(2);
                priceSymbol = bestListing.price.current.currency || 'ETH';
              }
              
              return { ...nft, price, priceSymbol };
            } catch (error) {
              console.error(`Error fetching listings for NFT ${nft.identifier}:`, error);
              return { ...nft, price: null, priceSymbol: 'ETH' };
            }
          })
        );
        
        if (resetData) {
          setNfts(nftsWithPrices);
        } else {
          setNfts(prevNfts => [...prevNfts, ...nftsWithPrices]);
        }
        
        setNextCursor(response.data.next);
        setCurrentContract(contractAddress);
      } else {
        throw new Error('Invalid response format from OpenSea API');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching NFTs:', error);
      setError(`Failed to fetch NFTs: ${error.message || 'Unknown error'}. Please try again.`);
      setLoading(false);
    }
  };

  // Load more NFTs when needed
  const loadMore = () => {
    if (nextCursor && currentContract) {
      fetchNFTs(currentContract, false);
    }
  };

  // Handle viewing NFT details
  const handleViewDetails = (nft) => {
    setDetailsNft(nft);
    setShowDetails(true);
  };

  // Handle closing NFT details
  const handleCloseDetails = () => {
    setShowDetails(false);
    setDetailsNft(null);
  };

  // Handle NFT purchase
  const handlePurchaseNFT = async (nft) => {
    if (!walletConnected || !signer) {
      setError('Please connect your wallet first');
      return;
    }

    try {
      setLoading(true);
      
      // Get the best listing for this NFT
      const nftIdentifier = nft.identifier || nft.id;
      const nftChain = nft.chain || selectedChain;
      const nftContract = nft.contract || currentContract;
      
      // Use the same listings URL pattern as in fetchFeaturedNFTs
      const listingsUrl = `https://api.opensea.io/api/v2/orders/${nftChain}/seaport/listings?asset_contract_address=${nftContract}&token_ids=${nftIdentifier}`;
      console.log(`Fetching listings from: ${listingsUrl}`);
      const response = await axios.get(listingsUrl);
      
      if (!response.data || !response.data.orders || response.data.orders.length === 0) {
        setError('No active listing found for this NFT');
        setLoading(false);
        return;
      }
      
      // Find the best listing (lowest price)
      const listings = response.data.orders;
      const bestListing = listings.sort((a, b) => {
        return parseFloat(a.current_price) - parseFloat(b.current_price);
      })[0];
      
      // Get fulfillment data
      const fulfillmentUrl = `https://api.opensea.io/api/v2/listings/${bestListing.order_hash}/fulfillment_data`;
      const fulfillmentResponse = await axios.post(fulfillmentUrl, {
        fulfiller: {
          address: account,
        }
      });
      
      if (!fulfillmentResponse.data || !fulfillmentResponse.data.transaction) {
        throw new Error('Failed to get fulfillment data');
      }
      
      // Create the transaction
      const tx = {
        to: fulfillmentResponse.data.transaction.to,
        from: fulfillmentResponse.data.transaction.from,
        data: fulfillmentResponse.data.transaction.data,
        value: fulfillmentResponse.data.transaction.value,
      };
      
      // Send the transaction
      const transaction = await signer.sendTransaction(tx);
      await transaction.wait();
      
      alert('Purchase successful!');
      // Refresh NFTs after purchase
      if (activeTab === 'explore') {
        fetchNFTs(currentContract);
      } else if (activeTab === 'featured') {
        fetchFeaturedNfts();
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error purchasing NFT:', error);
      setError(`Failed to purchase NFT: ${error.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  // Handle NFT listing for sale
  const openListingModal = (nft) => {
    setSelectedNft(nft);
    setShowModal(true);
    setListingPrice('');
  };

  const closeListingModal = () => {
    setShowModal(false);
    setSelectedNft(null);
  };

  const handleListNFT = async () => {
    if (!walletConnected || !signer || !selectedNft) {
      setError('Please connect your wallet first');
      return;
    }

    if (!listingPrice || parseFloat(listingPrice) <= 0) {
      setError('Please enter a valid price');
      return;
    }

    try {
      setLoading(true);
      
      // Convert price to Wei for future use
      // const priceInWei = ethers.utils.parseEther(listingPrice);
      const tokenId = selectedNft.identifier || selectedNft.token_id;
      
      // In a real implementation with the OpenSea API v2, you would:
      // 1. Create a properly formatted Seaport order
      // 2. Sign the order with the signer
      // 3. Submit the signed order to OpenSea's API
      
      // The actual implementation would use the listings create endpoint:
      // POST https://api.opensea.io/api/v2/listings
      
      // This would require creating a properly formatted orderParameters object,
      // generating a signature, and submitting both to the API
      
      console.log(`Would list NFT ${tokenId} from contract ${currentContract} for ${listingPrice} ETH on ${selectedChain}`);
      
      // For this example, we'll just show a success message since the full implementation
      // would require multiple additional steps to create a valid Seaport order
      alert(`NFT would be listed for ${listingPrice} ETH on ${selectedChain}`);
      
      closeListingModal();
      setLoading(false);
    } catch (error) {
      console.error('Error listing NFT:', error);
      setError(`Failed to list NFT for sale: ${error.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  // Handle navigation
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'featured') {
      fetchFeaturedNfts();
    }
  };

  // Render NFT cards
  const renderNftCard = (nft, index) => {
    // Get either image_url or display_image_url, with a placeholder as fallback
    const imageUrl = nft.image_url || nft.display_image_url || nft.image || 'https://via.placeholder.com/250';
    
    // Get the name from various possible sources
    const name = nft.name || `#${nft.identifier || nft.id}`;
    
    // Get the collection name - could be a string or an object
    const collection = typeof nft.collection === 'string' ? nft.collection : (nft.collection?.name || 'Unknown Collection');
    
    return (
      <div key={`${nft.contract}-${nft.identifier || nft.id}-${index}`} className="nft-card">
        <div className="nft-image">
          <img 
            src={imageUrl}
            alt={name}
            onError={(e) => {
              e.target.onerror = null; 
              e.target.src = 'https://via.placeholder.com/250?text=Image+Not+Available';
            }}
          />
        </div>
        <div className="nft-info">
          <h3>{name}</h3>
          <p className="collection">{collection}</p>
          {nft.price && (
            <p className="nft-price">{nft.price} {nft.priceSymbol}</p>
          )}
          {!nft.price && (
            <p className="nft-price">Not listed</p>
          )}
        </div>
        <div className="nft-actions">
          <button
            className="buy-button"
            onClick={() => handlePurchaseNFT(nft)}
            disabled={loading || !walletConnected || !nft.price}
          >
            Buy
          </button>
          <button
            className="details-button"
            onClick={() => handleViewDetails(nft)}
          >
            Details
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-left">
          <h1 className="logo">生</h1>
          <div className="search-bar">
            <input type="text" placeholder="Search collections and NFTs" />
            <button className="search-button">
              <span className="search-icon">🔍</span>
            </button>
          </div>
        </div>
        <div className="wallet-section">
          {walletConnected ? (
            <div className="wallet-info">
              <div className="wallet-balance">
                <img src="https://static.opensea.io/tokens/WETH.png" alt="ETH" className="token-icon" />
                <span>61.000 BERA</span>
              </div>
              <p className="connected-status">
                {account.substring(0, 6)}...{account.substring(account.length - 4)}
              </p>
              <button className="disconnect-button" onClick={() => setWalletConnected(false)}>
                <span className="menu-icon">≡</span>
              </button>
            </div>
          ) : (
            <button className="connect-button" onClick={connectWallet} disabled={loading}>
              {loading ? 'Connecting...' : 'Connect Wallet'}
            </button>
          )}
        </div>
      </header>

      <nav className="main-nav">
        <ul>
          <li className={activeTab === 'explore' ? 'active' : ''}>
            <button onClick={() => handleTabChange('explore')}>Explore</button>
          </li>
          <li className={activeTab === 'iconic' ? 'active' : ''}>
            <button onClick={() => handleTabChange('iconic')}>Iconic</button>
          </li>
          <li className={activeTab === 'featured' ? 'active' : ''}>
            <button onClick={() => handleTabChange('featured')}>Featured</button>
          </li>
          <li className={activeTab === 'about' ? 'active' : ''}>
            <button onClick={() => handleTabChange('about')}>About Us</button>
          </li>
        </ul>
      </nav>
      
      {activeTab === 'explore' && (
        <div className="chain-selector">
          <h3>Select Chain:</h3>
          <div className="chain-buttons">
            {chains.map((chain) => (
              <button
                key={chain.id}
                onClick={() => setSelectedChain(chain.id)}
                className={selectedChain === chain.id ? 'active' : ''}
                disabled={loading}
              >
                {chain.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <main className="container">
        {error && <div className="error-message">{error}</div>}

        {activeTab === 'explore' && (
          <>
            <div className="contract-selector">
              <h2>Select Collection</h2>
              <div className="contract-buttons">
                {contractAddresses.map((address) => (
                  <button
                    key={address}
                    onClick={() => fetchNFTs(address)}
                    className={currentContract === address ? 'active' : ''}
                    disabled={loading}
                  >
                    {address.substring(0, 6)}...{address.substring(address.length - 4)}
                  </button>
                ))}
              </div>
              <div className="add-contract">
                <input
                  type="text"
                  placeholder="Add contract address"
                  value={currentContract}
                  onChange={(e) => setCurrentContract(e.target.value)}
                />
                <button onClick={() => fetchNFTs(currentContract)} disabled={!currentContract || loading}>
                  Fetch NFTs
                </button>
              </div>
            </div>

            <div className="nfts-grid">
              {nfts.map((nft, index) => renderNftCard(nft, index))}
            </div>

            {nextCursor && (
              <div className="load-more">
                <button onClick={loadMore} disabled={loading}>
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}

            {nfts.length === 0 && !loading && currentContract && (
              <div className="no-nfts">
                <p>No NFTs found for this contract.</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'featured' && (
          <>
            <h1 className="section-title">Featured</h1>
            <div className="nfts-grid">
              {featuredNfts.map((nft, index) => renderNftCard(nft, index))}
            </div>
            
            {featuredNfts.length === 0 && !loading && (
              <div className="no-nfts">
                <p>No featured NFTs available.</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'iconic' && (
          <div className="coming-soon">
            <h2>Iconic Collections</h2>
            <p>This section is coming soon!</p>
          </div>
        )}
        
        {activeTab === 'about' && (
          <div className="about-section">
            <h2>About Us</h2>
            <p>We are a decentralized NFT marketplace built on the latest blockchain technology.</p>
            <p>Our mission is to provide a seamless experience for NFT collectors and creators.</p>
          </div>
        )}

        {loading && (
          <div className="loading">
            <p>Loading...</p>
          </div>
        )}

        {/* NFT Listing Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>List NFT for Sale</h2>
                <button className="close-button" onClick={closeListingModal}>×</button>
              </div>
              <div className="modal-content">
                {selectedNft && (
                  <div className="selected-nft">
                    <img 
                      src={selectedNft.image_url || selectedNft.display_image_url || 'https://via.placeholder.com/100'} 
                      alt={selectedNft.name || `NFT #${selectedNft.identifier}`} 
                    />
                    <div>
                      <h3>{selectedNft.name || `#${selectedNft.identifier}`}</h3>
                      <p>{selectedNft.collection}</p>
                    </div>
                  </div>
                )}
                <div className="price-input">
                  <label htmlFor="price">Price (ETH)</label>
                  <input
                    id="price"
                    type="number"
                    step="0.001"
                    min="0"
                    value={listingPrice}
                    onChange={(e) => setListingPrice(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="cancel-button" onClick={closeListingModal}>Cancel</button>
                <button
                  className="list-confirm-button"
                  onClick={handleListNFT}
                  disabled={loading || !listingPrice}
                >
                  {loading ? 'Processing...' : 'List for Sale'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* NFT Details View */}
        {showDetails && detailsNft && (
          <NFTDetails
            nft={detailsNft}
            onClose={handleCloseDetails}
            walletConnected={walletConnected}
            account={account}
          />
        )}
      </main>
    </div>
  );
}

export default App;